// Catalog Shell - DOM Utilities
// ============================================================================

import type { PaneTypeRenderContext } from '../core/types';

export function createElement<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    options?: {
        className?: string;
        id?: string;
        textContent?: string;
        innerHTML?: string;
        attributes?: Record<string, string>;
        style?: Partial<CSSStyleDeclaration>;
        children?: (Element | string)[];
        onClick?: (e: MouseEvent) => void;
        onContextMenu?: (e: MouseEvent) => void;
    }
): HTMLElementTagNameMap[K] {
    const el = document.createElement(tag);

    if (options?.className) {
        el.className = options.className;
    }

    if (options?.id) {
        el.id = options.id;
    }

    if (options?.textContent) {
        el.textContent = options.textContent;
    }

    if (options?.innerHTML) {
        el.innerHTML = options.innerHTML;
    }

    if (options?.attributes) {
        for (const [key, value] of Object.entries(options.attributes)) {
            el.setAttribute(key, value);
        }
    }

    if (options?.style) {
        Object.assign(el.style, options.style);
    }

    if (options?.children) {
        for (const child of options.children) {
            if (typeof child === 'string') {
                el.appendChild(document.createTextNode(child));
            } else {
                el.appendChild(child);
            }
        }
    }

    if (options?.onClick) {
        el.addEventListener('click', options.onClick as EventListener);
    }

    if (options?.onContextMenu) {
        el.addEventListener('contextmenu', options.onContextMenu as EventListener);
    }

    return el;
}

export function clearElement(el: HTMLElement): void {
    while (el.firstChild) {
        el.removeChild(el.firstChild);
    }
}

/**
 * Make `container`'s children exactly `desired`, in order, touching as little
 * as possible.
 *
 * `clearElement` + re-append (or `replaceChildren`) detaches EVERY child, even
 * one that is identical across renders. A detach collapses a text selection
 * anchored inside it, resets `:hover` until the next mousemove, drops the
 * focus of a button inside it and eats a click whose mousedown landed before
 * the rebuild. A list fed by live events (a chat thread, a log) does that to
 * its user on every event. This patches instead: a child present in both the
 * live DOM and `desired` at the same relative position is not touched at all,
 * so the caller keeps an element per row (keyed however it likes) and hands
 * back the same object for every row that did not change.
 *
 * O(n) with a Set. Promoted from `libs/agent-chat/src/dom-sync.ts`, where the
 * transcript renderer has relied on it since it was written.
 */
export function syncChildren(container: HTMLElement, desired: readonly HTMLElement[]): void {
    // Pass 1: drop strays, so pass 2's anchors are all wanted nodes.
    const want = new Set<Node>(desired);
    let node = container.firstChild;
    while (node) {
        const next = node.nextSibling;
        if (!want.has(node)) container.removeChild(node);
        node = next;
    }
    // Pass 2: walk the desired order, inserting/moving only mismatches. A match
    // advances the cursor without touching the node; `insertBefore` on a node
    // already elsewhere in the container moves it (detach + reattach for that
    // one node only, which is unavoidable: its position genuinely changed).
    let cursor: Node | null = container.firstChild;
    for (const el of desired) {
        if (el === cursor) {
            cursor = cursor.nextSibling;
            continue;
        }
        container.insertBefore(el, cursor);
    }
}

export function removeElement(el: HTMLElement): void {
    el.parentElement?.removeChild(el);
}

export function insertAfter(newNode: Node, referenceNode: Node): void {
    referenceNode.parentNode?.insertBefore(newNode, referenceNode.nextSibling);
}

export function insertBefore(newNode: Node, referenceNode: Node): void {
    referenceNode.parentNode?.insertBefore(newNode, referenceNode);
}

export function findAncestor(el: HTMLElement, predicate: (el: HTMLElement) => boolean): HTMLElement | null {
    let current: HTMLElement | null = el;
    while (current) {
        if (predicate(current)) {
            return current;
        }
        current = current.parentElement;
    }
    return null;
}

export function findAncestorByClass(el: HTMLElement, className: string): HTMLElement | null {
    return findAncestor(el, (e) => e.classList.contains(className));
}

export function getElementRect(el: HTMLElement): DOMRect {
    return el.getBoundingClientRect();
}

// Debounce function for resize handlers
export function debounce<T extends (...args: unknown[]) => void>(
    fn: T,
    delay: number
): (...args: Parameters<T>) => void {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Throttle function for frequent events
export function throttle<T extends (...args: unknown[]) => void>(
    fn: T,
    limit: number
): (...args: Parameters<T>) => void {
    let inThrottle = false;
    return (...args: Parameters<T>) => {
        if (!inThrottle) {
            fn(...args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
}

// Request animation frame with cancel support
export function rafLoop(callback: (delta: number) => boolean): () => void {
    let lastTime = performance.now();
    let rafId: number;
    let cancelled = false;

    const loop = (time: number) => {
        if (cancelled) return;
        const delta = time - lastTime;
        lastTime = time;
        const shouldContinue = callback(delta);
        if (shouldContinue) {
            rafId = requestAnimationFrame(loop);
        }
    };

    rafId = requestAnimationFrame(loop);

    return () => {
        cancelled = true;
        cancelAnimationFrame(rafId);
    };
}

// ============================================================================
// HTML & Pane Helpers
// ============================================================================

/** Escape HTML entities for safe string interpolation. */
export function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Get typed pane context from a render context.
 * Returns undefined if context is missing or empty.
 */
export function getTypedContext<T>(ctx: PaneTypeRenderContext): T | undefined {
    const c = ctx.instance.context;
    return (c && Object.keys(c).length > 0) ? c as unknown as T : undefined;
}
